import React from "react";
import * as Mui from "@mui/material";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { RootState } from "redux/store";
import { fetchIntegrations, saveIntegration } from "redux/slice/integrations";
import { getUser } from "redux/selector";
import { Integration } from "model/integration";
import * as api from "api/api";
import * as Icons from "react-feather";
import { urls } from "routes/urls";
import { AutoCreateToggle } from "./auto-create-toggle";
import { AgentSelectionTable } from "./agent-selection-table";
import { AgentSelectionState, NO_SELECTION_LOCKED_MESSAGE, SELECTION_LOCKED_MESSAGE, SELECTION_UNAVAILABLE_NOTE, buildSaveRequest, canContinue, initialSelectionState, isSelectionLocked, lockedPairs, validateSelection } from "./agent-selection";
import { FollowUpBossAgentSelection } from "model/follow-up-boss-agent-selection";
import { copyFor, isWhiteLabel } from "../../follow-up-boss-copy";
import { designTokens } from "theme";
import { Tag } from "component/shared/tag";
import { FormField } from "component/shared/form-fields";
import { effectiveBackfillStatus } from "../../follow-up-boss-status";
import { FUB_EMBEDDED_APP_URL, isEmbeddedAppEligible } from "../../follow-up-boss-embed-app";

const mapStateToProps = (state: RootState) => ({
    user: getUser(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
    fetchIntegrations,
    saveIntegration,
}, dispatch);

type AgentSync = "off" | "sync_data" | "sync_data_and_create";
type MergeStrategy = "fill_in_blanks" | "fub_wins" | "ihf_wins";

interface Props extends
    ReturnType<typeof mapStateToProps>,
    ReturnType<typeof mapDispatchToProps>
{
    integration: Integration;
    connected: boolean | null;
    webhooksMissing: number;
    webhookLastHealthCheckDate: number | null;
    agentsMatched: number | null;
    ihfAgentsNotInFub: number | null;
    fubUsersNotInIhf: number | null;
    leadsMatched: number | null;
    leadsUnmatched: number | null;
    backfillStatus: "not_started" | "running" | "complete" | "failed" | null;
    agentSync: AgentSync;
    apiKeyValid: boolean | null;
    lastWebhookProcessed: number | null;
    lastSyncDate: number | null;
    errorCount24h: number;
    backfillLeadsStatus: string | null;
    backfillEventsStatus: string | null;
    // MAX-4236: shared with the Settings tab through the modal, so switching tabs does not
    // repeat the selection GET (which calls Follow Up Boss live).
    cachedSelection?: FollowUpBossAgentSelection | null;
    onSelectionLoaded?: (selection: FollowUpBossAgentSelection) => void;
    onStatusChange: (patch: {
        connected?: boolean;
        webhooksMissing?: number;
        webhookLastHealthCheckDate?: number | null;
        agentsMatched?: number | null;
        ihfAgentsNotInFub?: number | null;
        fubUsersNotInIhf?: number | null;
        leadsMatched?: number | null;
        leadsUnmatched?: number | null;
        backfillStatus?: "not_started" | "running" | "complete" | "failed" | null;
        agentSync?: AgentSync;
        apiKeyValid?: boolean | null;
        lastWebhookProcessed?: number | null;
        lastSyncDate?: number | null;
        errorCount24h?: number;
        backfillLeadsStatus?: string | null;
        backfillEventsStatus?: string | null;
        mergeStrategy?: MergeStrategy;
    }) => void;
    onClose?: () => void;
    onWizardComplete?: () => void;
}

interface State {
    // Wizard
    wizardStep: number;
    apiKey: string;
    connecting: boolean;
    connectError: string | null;
    mergeStrategy: MergeStrategy;
    agentSyncCreate: boolean;
    setupComplete: boolean;
    syncStarted: boolean;
    backfilling: boolean;
    // MAX-4236 agent selection (wizard step 2, selection mode only)
    selection: FollowUpBossAgentSelection | null;
    selectionState: AgentSelectionState | null;
    selectionLoading: boolean;
    selectionError: string | null;
    selectionSaving: boolean;
    // Status tab accordions
    leadsOpen: boolean;
    agentsOpen: boolean;
    // Backfill polling
    pollLeadsStatus: string | null;
    pollEventsStatus: string | null;
    pollIntervalId: ReturnType<typeof setInterval> | null;
}

const WIZARD_STEPS = [
    "Connect via API Key",
    "Set Sync Preferences",
    "Start Sync",
];

// Status tab rows are deliberately styled unlike input fields (soft filled background)
const statusRowStyle: React.CSSProperties = {
    backgroundColor: "#f7f7f7",
    borderRadius: 8,
    padding: "16px 20px",
};

// Sticky action bar: footer buttons stay visible while the modal body scrolls (MAX-3998).
// 16px margin + 16px padding gives the design's 32px gap from the end of content.
const stickyFooterStyle: React.CSSProperties = {
    position: "sticky",
    bottom: 0,
    backgroundColor: "#fff",
    marginTop: 16,
    paddingTop: 16,
    paddingBottom: 16,
    zIndex: 1,
};

class Component extends React.Component<Props, State> {

    public constructor(props: Props) {
        super(props);
        this.state = {
            wizardStep: 0,
            apiKey: (props.integration.data?.externalKey as string) ?? "",
            connecting: false,
            connectError: null,
            mergeStrategy: (props.integration.data?.followUpBossMergeStrategy as MergeStrategy) ?? "fill_in_blanks",
            agentSyncCreate: (props.integration.data?.followUpBossAgentSyncMode as string) !== "sync_data",
            setupComplete: false,
            syncStarted: false,
            backfilling: false,
            selection: null,
            selectionState: null,
            selectionLoading: false,
            selectionError: null,
            selectionSaving: false,
            leadsOpen: false,
            agentsOpen: false,
            pollLeadsStatus: null,
            pollEventsStatus: null,
            pollIntervalId: null,
        };
    }

    // MAX-4580: the Status tab lists the selected Follow Up Boss users and their agents, so the
    // selection is needed on the connected path too, not just after the wizard's connect step.
    public componentDidMount() {
        this.maybeLoadSelectionForStatus();
    }

    // `connected` starts null while the status GET is in flight, and the modal can be opened in
    // that window, so mount alone would miss the null -> true transition and leave the Agents
    // section stuck with nothing to show.
    public componentDidUpdate(prevProps: Props) {
        if (prevProps.connected !== this.props.connected) {
            this.maybeLoadSelectionForStatus();
        }
    }

    private maybeLoadSelectionForStatus() {
        if (!this.props.connected || !this.isSelectionMode() || this.state.selection || this.state.selectionLoading) {
            return;
        }
        const cached = this.props.cachedSelection;
        if (cached) {
            this.setState({ selection: cached, selectionState: initialSelectionState(cached) });
        } else {
            this.loadAgentSelection();
        }
    }

    public componentWillUnmount() {
        this.stopPolling();
    }

    private stopPolling() {
        if (this.state.pollIntervalId !== null) {
            clearInterval(this.state.pollIntervalId);
            this.setState({ pollIntervalId: null });
        }
    }

    private startPolling() {
        this.stopPolling();
        const intervalId = setInterval(() => this.pollStatus(), 5000);
        this.setState({ pollIntervalId: intervalId });
    }

    private async pollStatus() {
        const { integration, onStatusChange } = this.props;
        try {
            const status = await api.getFollowUpBossStatus(integration.id);
            const leadsStatus = status.backfillLeadsStatus ?? null;
            const eventsStatus = status.backfillEventsStatus ?? null;
            this.setState({ pollLeadsStatus: leadsStatus, pollEventsStatus: eventsStatus });
            // Reconcile the parent's optimistic "running" with the backend's real state —
            // otherwise a failed or already-finished backfill keeps showing "Syncing…" and
            // stale counts until the modal is reopened
            onStatusChange({
                backfillStatus: effectiveBackfillStatus(status.backfillStatus ?? null, leadsStatus),
                agentsMatched: status.agentsMatched ?? null,
                ihfAgentsNotInFub: status.ihfAgentsNotInFub ?? null,
                fubUsersNotInIhf: status.fubUsersNotInIhf ?? null,
                leadsMatched: status.leadsMatched ?? null,
                leadsUnmatched: status.leadsUnmatched ?? null,
                errorCount24h: status.errorCount24h ?? 0,
                lastWebhookProcessed: status.lastWebhookProcessed ?? null,
                lastSyncDate: status.lastSyncDate ?? null,
            });
            if (leadsStatus !== "running" && eventsStatus !== "running") {
                this.stopPolling();
            }
        } catch(_e) {
            // silently ignore
        }
    }

    // Step 1: validate the API key
    private async handleValidateApiKey() {
        const { integration, fetchIntegrations } = this.props;
        const { apiKey } = this.state;
        this.setState({ connecting: true, connectError: null });
        try {
            await api.connectFollowUpBoss(integration.id, apiKey);
            fetchIntegrations();
            this.setState({ connecting: false, wizardStep: 1 });
            // The pairs the table pre-fills from are written by connect(), so the GET waits for it
            if (this.isSelectionMode()) {
                this.loadAgentSelection();
            }
        } catch(e) {
            // The backend returns user-presentable messages (invalid key, account already
            // connected elsewhere, FUB webhook limit) — show them instead of assuming
            // every failure is a bad key (MAX-4012)
            const serverMessage = e instanceof Error && e.message ? e.message : null;
            this.setState({ connecting: false, connectError: serverMessage ?? "Invalid API key. Verify your key is correct in Follow Up Boss under Admin → API." });
        }
    }

    // Auto-create requires the FollowUpBossAgentSync billing feature; without it the
    // toggle is hidden and the setting can only be saved as sync_data
    private isAgentSyncPermitted(): boolean {
        return !!this.props.user.permissions.followUpBossAgentSync;
    }

    // MAX-4028: fixed-seat plans can't add agent seats — auto-create is locked OFF.
    // Evaluated live from permissions so a plan upgrade unlocks it on next load.
    private isFixedSeatPlan(): boolean {
        return this.props.user.permissions.agentSeatCap != null;
    }

    // MAX-4236: the FollowUpBossAgentSelection billing feature. The customer picks which FUB
    // users import. MAX-4579: the Agent Matching section is not shown at all in this mode (the
    // server never auto-creates agents here), and the selection locks once saved.
    private isSelectionMode(): boolean {
        return !!this.props.user.permissions.followUpBossAgentSelection;
    }

    private async loadAgentSelection() {
        const { integration } = this.props;
        this.setState({ selectionLoading: true, selectionError: null });
        try {
            const selection = await api.getFollowUpBossAgentSelection(integration.id);
            this.setState({ selection, selectionState: initialSelectionState(selection), selectionLoading: false });
            this.props.onSelectionLoaded?.(selection);
        } catch(e) {
            const serverMessage = e instanceof Error && e.message ? e.message : null;
            this.setState({ selection: null, selectionState: null, selectionLoading: false, selectionError: serverMessage ?? "Could not load Follow Up Boss users." });
        }
    }

    // Step 2 Continue: in selection mode the selection is saved here (R3), where cap and
    // conflict errors have a place to show; Step 3 is unchanged.
    private async handleStep2Continue() {
        if (!this.isSelectionMode()) {
            this.setState({ wizardStep: 2 });
            return;
        }
        const { integration } = this.props;
        const { selection, selectionState } = this.state;
        if (!selection || !selectionState) {
            // The selection GET failed (FUB rate limit or outage). Unconfirmed means ungated, so
            // continuing without a save is safe; the selection can be made on the Settings tab.
            this.setState({ wizardStep: 2 });
            return;
        }
        if (isSelectionLocked(selection)) {
            // Already saved on this visit (Step 3 Back, then Continue): the server answers 409 to
            // a second PUT, and the table above is read-only, so just move on.
            this.setState({ wizardStep: 2 });
            return;
        }
        const clientError = validateSelection(selectionState, selection.seatCap, isWhiteLabel(this.props.user));
        if (clientError) {
            this.setState({ selectionError: clientError });
            return;
        }
        this.setState({ selectionSaving: true, selectionError: null });
        try {
            const saved = await api.saveFollowUpBossAgentSelection(integration.id, buildSaveRequest(selectionState));
            this.setState({ selection: saved, selectionState: initialSelectionState(saved), selectionSaving: false, wizardStep: 2 });
            this.props.onSelectionLoaded?.(saved);
        } catch(e) {
            const serverMessage = e instanceof Error && e.message ? e.message : null;
            this.setState({ selectionSaving: false, selectionError: serverMessage ?? "Saving the selection failed. Please try again." });
        }
    }

    // Step 3 / connected view: save preferences and start backfill
    private async handleStartSync() {
        const { integration, saveIntegration, onStatusChange } = this.props;
        const { mergeStrategy, agentSyncCreate } = this.state;
        const agentSync: AgentSync = this.isAgentSyncPermitted() && !this.isFixedSeatPlan() && !this.isSelectionMode() && agentSyncCreate ? "sync_data_and_create" : "sync_data";
        const inWizard = this.props.connected === false;
        this.setState({ backfilling: true });
        try {
            await saveIntegration({ integration: { ...integration, data: {
                followUpBossMergeStrategy: mergeStrategy,
                followUpBossAgentSyncMode: agentSync,
            }}});
            onStatusChange({ mergeStrategy, agentSync, ...(inWizard ? {} : { connected: true }) });
            await api.startFollowUpBossBackfill(integration.id);
        } catch(_e) {
            // fall through — surface the syncing state either way and let polling settle it
        }
        onStatusChange({ backfillStatus: "running", ...(inWizard ? {} : { connected: true }) });
        this.setState({ backfilling: false, syncStarted: true, setupComplete: inWizard || this.state.setupComplete });
        this.startPolling();
    }

    // Step 3: persist the wizard's sync preferences without starting the backfill.
    // Without this, "Sync Later" would silently discard step-2 choices and the
    // Settings tab would fall back to its own (different) defaults.
    private async handleSyncLater() {
        const { integration, saveIntegration, onStatusChange } = this.props;
        const { mergeStrategy, agentSyncCreate } = this.state;
        // fixed-seat and selection-mode guards match handleStartSync so Sync Later cannot persist a locked-off create
        const agentSync: AgentSync = this.isAgentSyncPermitted() && !this.isFixedSeatPlan() && !this.isSelectionMode() && agentSyncCreate ? "sync_data_and_create" : "sync_data";
        try {
            await saveIntegration({ integration: { ...integration, data: {
                followUpBossMergeStrategy: mergeStrategy,
                followUpBossAgentSyncMode: agentSync,
            }}});
            onStatusChange({ mergeStrategy, agentSync });
        } catch(_e) {
            // fall through — the user can still adjust preferences from the Settings tab
        }
        this.setState({ setupComplete: true, syncStarted: false });
    }

    // Completion screen: drop into the connected Status tab
    private handleFinishSetup() {
        const { onStatusChange, onWizardComplete } = this.props;
        const { syncStarted } = this.state;
        onStatusChange({ connected: true, ...(syncStarted ? {} : { backfillStatus: "not_started" }) });
        if (onWizardComplete) onWizardComplete();
    }

    private formatDate(epochMs: number): string {
        return new Date(epochMs).toLocaleDateString("en-US", {
            month: "numeric", day: "numeric", year: "numeric",
            hour: "numeric", minute: "2-digit",
        });
    }

    private renderMergeStrategyRadios() {
        const copy = copyFor(this.props.user);
        const { mergeStrategy } = this.state;
        return (
            <Mui.RadioGroup
                value={mergeStrategy}
                onChange={(e) => this.setState({ mergeStrategy: e.target.value as MergeStrategy })}
            >
                {([
                    {
                        value: "fill_in_blanks",
                        label: "Fill missing fields only",
                        recommended: true,
                        description: "If a field is empty in one system but has a value in the other, the empty field is filled in. Existing data in either system is never overwritten.",
                    },
                    {
                        value: "ihf_wins",
                        label: copy.conflictIhfLabel,
                        description: copy.conflictIhfWizard,
                    },
                    {
                        value: "fub_wins",
                        label: "Prioritize Follow Up Boss data",
                        description: copy.conflictFubWizard,
                    },
                ] as { value: string; label: string; recommended?: boolean; description: string }[]).map((opt, idx, arr) => (
                    <Mui.FormControlLabel
                        key={opt.value}
                        value={opt.value}
                        control={<Mui.Radio color="secondary" size="small" style={{ alignSelf: "flex-start", paddingTop: 4 }} />}
                        style={{ alignItems: "flex-start", marginBottom: idx === arr.length - 1 ? 0 : 24 }}
                        label={
                            <Mui.Box style={{ paddingTop: 2 }}>
                                <Mui.Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: "wrap" }}>
                                    <Mui.Typography variant="body2" style={{ fontWeight: 600, color: "#1C1B1F" }}>{opt.label}</Mui.Typography>
                                    {opt.recommended && (
                                        <Tag label="Recommended" variant="success" size="small" />
                                    )}
                                </Mui.Box>
                                <Mui.Typography variant="body2" style={{ marginTop: 8, color: "#1C1B1F" }}>{opt.description}</Mui.Typography>
                            </Mui.Box>
                        }
                    />
                ))}
            </Mui.RadioGroup>
        );
    }

    // "You're all set!" — shown after Start Sync or Sync Later; the install-embedded-app
    // callout lives here so the wizard never sends users out of control panel mid-setup.
    // The callout is hidden entirely for accounts the embedded app isn't available to
    // (MAX-4233), so Standard/v9 accounts never get a link that dead-ends in FUB.
    private renderComplete() {
        const { syncStarted } = this.state;
        // MAX-4580: resellers are not told to install the embedded app. The page it links to is
        // hosted by Follow Up Boss and is iHomefinder branded, so there is no neutral wording.
        const showEmbeddedAppCallout = isEmbeddedAppEligible(this.props.user) && !isWhiteLabel(this.props.user);
        return (
            <>
            <Mui.Grid container direction="column" spacing={2}>
                <Mui.Grid item>
                    <Mui.Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Icons.CheckCircle size={22} style={{ color: "#2e7d32" }} />
                        <Mui.Typography variant="h6" style={{ fontWeight: 700 }}>You're all set!</Mui.Typography>
                    </Mui.Box>
                </Mui.Grid>
                <Mui.Grid item>
                    <Mui.Typography variant="body2">
                        {syncStarted
                            ? "Your Follow Up Boss integration is connected and sync is in progress. Depending on how many records you have, this may take some time — it runs in the background so you can check back later."
                            : "Your Follow Up Boss integration is connected. Start your sync from the integration page when you're ready."}
                    </Mui.Typography>
                </Mui.Grid>
                {showEmbeddedAppCallout && (
                    <>
                    <Mui.Grid item>
                        <Mui.Typography variant="body2">
                            <strong>One more thing:</strong> Install the iHomefinder app in Follow Up Boss to manage your leads' saved searches, favorite properties, and market reports without switching platforms.
                        </Mui.Typography>
                    </Mui.Grid>
                    <Mui.Grid item>
                        <Mui.Link
                            href={FUB_EMBEDDED_APP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            style={{ fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}
                        >
                            Install the iHomefinder app in Follow Up Boss <Icons.ExternalLink size={16} />
                        </Mui.Link>
                    </Mui.Grid>
                    </>
                )}
            </Mui.Grid>
            <Mui.Box display="flex" justifyContent="flex-end" style={stickyFooterStyle}>
                <Mui.Button
                    variant="contained"
                    color="primary"
                    onClick={() => this.handleFinishSetup()}
                >
                    View Integration
                </Mui.Button>
            </Mui.Box>
            </>
        );
    }

    private renderWizard() {
        const copy = copyFor(this.props.user);
        const { onClose } = this.props;
        const { apiKey, connecting, connectError, wizardStep, backfilling } = this.state;
        const { selection, selectionState, selectionLoading, selectionError, selectionSaving } = this.state;
        const selectionMode = this.isSelectionMode();
        // Blocked while a request is in flight or a selected user has no agent yet (MAX-4579);
        // a failed selection load falls through to Step 3 (see handleStep2Continue)
        const continueBlocked = selectionMode && (selectionLoading || selectionSaving || !canContinue(selectionState, selection?.seatCap));

        return (
            <Mui.Box>
                <Mui.Typography style={{ fontSize: 16, lineHeight: "24px", color: "#5F6368", marginBottom: 16 }}>
                    Step {wizardStep + 1} of {WIZARD_STEPS.length}
                </Mui.Typography>
                {/* Step 3's title sits 16px above the "Before syncing" banner; other steps keep 24px */}
                <Mui.Typography variant="h6" style={{ fontWeight: 700, fontSize: 18, marginBottom: wizardStep === 2 ? 16 : 24 }}>
                    {WIZARD_STEPS[wizardStep]}
                </Mui.Typography>

                {/* Step 1: API Key */}
                {wizardStep === 0 && (
                    <Mui.Grid container direction="column" spacing={2}>
                        <Mui.Grid item>
                            <Mui.Typography variant="body2">
                                {copy.apiKeyScope}
                            </Mui.Typography>
                        </Mui.Grid>
                        <Mui.Grid item>
                            <Mui.Typography variant="body2">
                                <strong>This integration must be set up by the Follow Up Boss account owner.</strong> Only the account owner's API key can be used to connect.
                            </Mui.Typography>
                        </Mui.Grid>
                        <Mui.Grid item>
                            <Mui.Typography variant="body2">
                                Enter your Follow Up Boss API key below. To locate your API key, navigate to <strong>Admin → API</strong> in your Follow Up Boss account.
                            </Mui.Typography>
                        </Mui.Grid>
                        <Mui.Grid item>
                            <FormField
                                label="API Key"
                                value={apiKey}
                                onChange={(e) => this.setState({ apiKey: e.target.value })}
                                placeholder="fka_000000000000000000000000"
                                disabled={connecting}
                                error={!!connectError}
                            />
                            {connectError && (
                                <Mui.Box display="flex" alignItems="center" style={{ gap: 8, marginTop: 8 }}>
                                    <Icons.XCircle size={16} style={{ color: "#EF4444", flexShrink: 0 }} />
                                    <Mui.Typography style={{ fontSize: 16, lineHeight: "24px", color: "#5F6368" }}>
                                        {connectError}
                                    </Mui.Typography>
                                </Mui.Box>
                            )}
                        </Mui.Grid>
                    </Mui.Grid>
                )}
                {wizardStep === 0 && (
                    <Mui.Box display="flex" justifyContent="flex-end" style={{ ...stickyFooterStyle, gap: 8 }}>
                        <Mui.Button
                            variant="outlined"
                            color="primary"
                            style={{ padding: "7px 16px" }}
                            onClick={() => onClose && onClose()}
                            disabled={connecting}
                        >
                            {apiKey.trim() ? "← Back" : "Cancel"}
                        </Mui.Button>
                        <Mui.Button
                            variant="contained"
                            color="primary"
                            onClick={() => this.handleValidateApiKey()}
                            disabled={connecting || !apiKey.trim()}
                        >
                            {connecting ? (
                                <><Mui.CircularProgress size={16} style={{ marginRight: 8 }} />Validating API Key…</>
                            ) : "Continue →"}
                        </Mui.Button>
                    </Mui.Box>
                )}

                {/* Step 2: Set Sync Preferences — 24px between content and dividers */}
                {wizardStep === 1 && (
                    <>
                        {/* MAX-4236: which FUB users import, mapped to IHF agents (selection mode only) */}
                        {selectionMode && (
                            <>
                                <Mui.Typography variant="subtitle1" style={{ fontWeight: 700, fontSize: 16, color: "#1C1B1F", marginBottom: 16 }}>Agent Selection</Mui.Typography>
                                <AgentSelectionTable
                                    selection={selection}
                                    state={selectionState}
                                    loading={selectionLoading}
                                    error={selectionError}
                                    disabled={selectionSaving}
                                    readOnly={isSelectionLocked(selection)}
                                    onChange={(next) => this.setState({ selectionState: next, selectionError: null })}
                                    onRetry={() => this.loadAgentSelection()}
                                    unavailableNote={SELECTION_UNAVAILABLE_NOTE}
                                    showEmptyWarning
                                    whiteLabel={isWhiteLabel(this.props.user)}
                                />
                                <Mui.Divider style={{ margin: "24px 0" }} />
                            </>
                        )}
                        {/* MAX-4579: no Agent Matching section in selection mode — auto-create is never offered there */}
                        {this.isAgentSyncPermitted() && !selectionMode && (
                            <>
                                <Mui.Typography variant="subtitle1" style={{ fontWeight: 700, fontSize: 16, color: "#1C1B1F", marginBottom: 24 }}>Agent Matching</Mui.Typography>
                                <AutoCreateToggle
                                    checked={!this.isFixedSeatPlan() && this.state.agentSyncCreate}
                                    onChange={(checked) => this.setState({ agentSyncCreate: checked })}
                                    lockedOff={this.isFixedSeatPlan()}
                                    whiteLabel={isWhiteLabel(this.props.user)}
                                />
                                <Mui.Divider style={{ margin: "24px 0" }} />
                            </>
                        )}
                        <Mui.Typography variant="subtitle1" style={{ fontWeight: 700, fontSize: 16, color: "#1C1B1F", marginBottom: 8 }}>Data Conflicts</Mui.Typography>
                        <Mui.Typography variant="body2" style={{ color: "#1C1B1F", marginBottom: 8 }}>
                            These options only apply when all records sync at once (initial sync or Sync Now). They don't affect individual record updates, which sync automatically based on the most recent change.
                        </Mui.Typography>
                        <Mui.Typography variant="body2" style={{ color: "#1C1B1F", marginBottom: 24 }}>
                            {selectionMode
                                ? copy.conflictSelectionNote
                                : "New leads missing from either system are still created automatically."}
                        </Mui.Typography>
                        {this.renderMergeStrategyRadios()}
                        <Mui.Box display="flex" justifyContent="flex-end" style={{ ...stickyFooterStyle, gap: 8 }}>
                            <Mui.Button
                                variant="outlined"
                                color="primary"
                                style={{ padding: "7px 16px" }}
                                onClick={() => this.setState({ wizardStep: 0 })}
                                disabled={selectionSaving}
                            >
                                ← Back
                            </Mui.Button>
                            <Mui.Button
                                variant="contained"
                                color="primary"
                                onClick={() => this.handleStep2Continue()}
                                disabled={continueBlocked}
                            >
                                {selectionSaving ? (
                                    <><Mui.CircularProgress size={16} style={{ marginRight: 8 }} />Saving…</>
                                ) : "Continue →"}
                            </Mui.Button>
                        </Mui.Box>
                    </>
                )}

                {/* Step 3: Start Sync */}
                {wizardStep === 2 && (
                    <Mui.Grid container direction="column" spacing={2}>
                        <Mui.Grid item>
                            <Mui.Box style={{ backgroundColor: "#fff8e1", borderRadius: 4, padding: "12px 16px" }}>
                                <Mui.Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 6 }}>
                                    <Icons.AlertTriangle size={16} style={{ color: "#f57f17", flexShrink: 0 }} />
                                    <Mui.Typography variant="body2" style={{ fontWeight: 700 }}>Before syncing, make sure:</Mui.Typography>
                                </Mui.Box>
                                <ul style={{ margin: 0, paddingLeft: 24 }}>
                                    <li>
                                        <Mui.Typography variant="body2">
                                            Any user who owns leads is set up as an <strong>Agent</strong>{copy.step3AgentBulletTail}
                                        </Mui.Typography>
                                    </li>
                                    <li>
                                        <Mui.Typography variant="body2">
                                            Agent and lead email addresses match in both systems — records are matched automatically by email.
                                        </Mui.Typography>
                                    </li>
                                    <li>
                                        <Mui.Typography variant="body2">
                                            {copy.step3LeadAssignment}
                                        </Mui.Typography>
                                    </li>
                                </ul>
                            </Mui.Box>
                        </Mui.Grid>
                        <Mui.Grid item>
                            <Mui.Typography variant="body2">
                                {selectionMode
                                    ? copy.step3SelectionSummary
                                    : "Syncing will create leads in either system where they don't exist and keep records up to date across both platforms. Here's what will be included:"}
                            </Mui.Typography>
                        </Mui.Grid>
                        <Mui.Grid item>
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                <li><Mui.Typography variant="body2"><strong>Leads</strong> — {selectionMode
                                    ? copy.step3SelectionLeads
                                    : "new leads are created in both systems; contact details, agent assignments, tags, and notes stay in sync."}</Mui.Typography></li>
                                <li><Mui.Typography variant="body2"><strong>Agents</strong> — matched accounts based on email between both systems</Mui.Typography></li>
                                <li><Mui.Typography variant="body2"><strong>Activity</strong> — lead registration and behavioral events (Premium only)</Mui.Typography></li>
                            </ul>
                        </Mui.Grid>
                        <Mui.Grid item>
                            <Mui.Typography variant="body2" style={{ color: "#1C1B1F" }}>
                                Not ready yet? You may want to sync later if you'd like to clean up your lead or agent records in either system before importing.
                            </Mui.Typography>
                        </Mui.Grid>
                    </Mui.Grid>
                )}
                {wizardStep === 2 && (
                    <Mui.Box display="flex" justifyContent="space-between" style={stickyFooterStyle}>
                        <Mui.Button
                            variant="outlined"
                            color="primary"
                            style={{ padding: "7px 16px" }}
                            onClick={() => this.setState({ wizardStep: 1 })}
                        >
                            ← Back
                        </Mui.Button>
                        <Mui.Box display="flex" style={{ gap: 8 }}>
                            <Mui.Button
                                variant="outlined"
                                color="primary"
                                style={{ padding: "7px 16px" }}
                                onClick={() => this.handleSyncLater()}
                            >
                                Sync Later
                            </Mui.Button>
                            <Mui.Button
                                variant="contained"
                                color="primary"
                                disabled={backfilling}
                                onClick={() => this.handleStartSync()}
                            >
                                {backfilling ? <Mui.CircularProgress size={18} /> : "Start Sync"}
                            </Mui.Button>
                        </Mui.Box>
                    </Mui.Box>
                )}
            </Mui.Box>
        );
    }

    private renderStatusChip() {
        const { webhooksMissing, backfillStatus, apiKeyValid } = this.props;
        // apiKeyValid is checked live against FUB on every status poll — a revoked key
        // must not keep showing "Connected" until the nightly health check (MAX-4007)
        if (webhooksMissing > 0 || apiKeyValid === false) {
            return (
                <Mui.Chip
                    label="Connection Lost"
                    size="small"
                    icon={<Icons.AlertTriangle size={12} style={{ color: "#b71c1c", marginLeft: 8 }} />}
                    style={{ backgroundColor: "#fdecea", color: "#b71c1c" }}
                />
            );
        }
        if (backfillStatus === "running") {
            return (
                <Mui.Chip
                    label="Syncing…"
                    size="small"
                    icon={<Mui.CircularProgress size={11} style={{ marginLeft: 8 }} />}
                    style={{ backgroundColor: "#eeeeee", color: "#424242" }}
                />
            );
        }
        if (backfillStatus === "not_started") {
            return (
                <Mui.Chip
                    label="Ready to Sync"
                    size="small"
                    icon={<Icons.Clock size={11} style={{ color: "#00695c", marginLeft: 8 }} />}
                    style={{ backgroundColor: "#e0f2f1", color: "#00695c" }}
                />
            );
        }
        // A failed backfill must not fall through to the green Connected chip (MAX-4043)
        if (backfillStatus === "failed") {
            return (
                <Mui.Chip
                    label="Sync Failed"
                    size="small"
                    icon={<Icons.AlertTriangle size={12} style={{ color: "#b71c1c", marginLeft: 8 }} />}
                    style={{ backgroundColor: "#fdecea", color: "#b71c1c" }}
                />
            );
        }
        return (
            <Mui.Chip
                label="Connected"
                size="small"
                icon={<Icons.Check size={11} style={{ color: "#2e7d32", marginLeft: 8 }} />}
                style={{ backgroundColor: "#e8f5e9", color: "#2e7d32" }}
            />
        );
    }

    private renderConnected() {
        const copy = copyFor(this.props.user);
        const {
            lastWebhookProcessed, lastSyncDate, errorCount24h, backfillStatus,
            agentsMatched, ihfAgentsNotInFub, fubUsersNotInIhf,
            leadsMatched, leadsUnmatched,
        } = this.props;
        const { leadsOpen, agentsOpen, selection } = this.state;

        // MAX-4580: on agent-selection plans the Agents section lists the selected Follow Up Boss
        // users and their agents instead of matched/unmatched counts. Those counts are measured
        // across every user on the Follow Up Boss account, most of which are deliberately not
        // this customer's, so they read as alarming noise on exactly these accounts.
        const selectionMode = this.isSelectionMode();
        // Only a *confirmed* selection is shown. Until the first save the server's pairs are a
        // suggestion the customer has not agreed to (an account connected before its package
        // gained agent selection chooses in Settings), so listing them under "Agents can't be
        // changed after setup" would assert a lock that does not exist. The selection GET also
        // calls Follow Up Boss live and can fail while the rest of the tab is fine. Both cases
        // collapse the section to its header rather than erroring the tab.
        const selectionLocked = selectionMode && isSelectionLocked(selection);
        const selectedPairs = selectionLocked && selection ? lockedPairs(selection) : [];
        const agentsExpandable = selectionMode ? selectionLocked : agentsMatched !== null;
        const agentsSummary = selectionMode
            ? (selectionLocked ? `${selectedPairs.length} selected` : "–")
            : (agentsMatched !== null ? `${agentsMatched} synced` : "–");

        const isReadyToSync = backfillStatus === "not_started";
        const agentsUnmatched = agentsMatched !== null
            ? (ihfAgentsNotInFub ?? 0) + (fubUsersNotInIhf ?? 0)
            : null;

        return (
            // Bottom padding is local because DialogContent has none — the sticky-footer
            // tabs carry their own (MAX-4008)
            <Mui.Grid container direction="column" spacing={2} style={{ paddingBottom: 16 }}>

                {/* Ready-to-sync banner */}
                {isReadyToSync && (
                    <Mui.Grid item>
                        <Mui.Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            style={{ backgroundColor: "#e7f4f8", borderRadius: 8, padding: "12px 16px" }}
                        >
                            <Mui.Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                <Icons.Clock size={16} style={{ color: "#00695c", flexShrink: 0 }} />
                                <Mui.Typography variant="body2">
                                    {copy.statusReadyToSync}
                                </Mui.Typography>
                            </Mui.Box>
                            <Mui.Button
                                variant="contained"
                                color="primary"
                                size="small"
                                style={{ marginLeft: 16, whiteSpace: "nowrap" }}
                                onClick={() => this.handleStartSync()}
                            >
                                Start Sync
                            </Mui.Button>
                        </Mui.Box>
                    </Mui.Grid>
                )}

                {/* Overview header */}
                <Mui.Grid item>
                    <Mui.Box display="flex" alignItems="center" justifyContent="space-between">
                        <Mui.Typography variant="subtitle1" style={{ fontWeight: 700, fontSize: 18 }}>Overview</Mui.Typography>
                        {!isReadyToSync && backfillStatus !== "running" && (
                            <Mui.Button
                                variant="text"
                                color="primary"
                                onClick={() => this.handleStartSync()}
                            >
                                Sync Now <Icons.RefreshCw size={16} style={{ marginLeft: 4 }} />
                            </Mui.Button>
                        )}
                    </Mui.Box>
                </Mui.Grid>

                {/* Status */}
                <Mui.Grid item>
                    <Mui.Box display="flex" alignItems="center" justifyContent="space-between" style={statusRowStyle}>
                        <Mui.Box display="flex" alignItems="center" style={{ gap: 10 }}>
                            <Icons.Zap size={16} style={{ color: "#424242" }} />
                            <Mui.Typography variant="body1" style={{ fontWeight: 700 }}>Status</Mui.Typography>
                        </Mui.Box>
                        {this.renderStatusChip()}
                    </Mui.Box>
                </Mui.Grid>

                {/* Last Sync */}
                <Mui.Grid item>
                    <Mui.Box display="flex" alignItems="center" justifyContent="space-between" style={statusRowStyle}>
                        <Mui.Box display="flex" alignItems="center" style={{ gap: 10 }}>
                            <Icons.RefreshCw size={16} style={{ color: "#424242" }} />
                            <Mui.Typography variant="body1" style={{ fontWeight: 700 }}>Last Sync</Mui.Typography>
                        </Mui.Box>
                        <Mui.Typography variant="body1">
                            {/* Prefer the backfill completion stamp; ongoing webhook activity is the fallback */}
                            {(lastSyncDate ?? lastWebhookProcessed) !== null ? this.formatDate((lastSyncDate ?? lastWebhookProcessed)!) : "–"}
                        </Mui.Typography>
                    </Mui.Box>
                </Mui.Grid>

                {/* Errors */}
                <Mui.Grid item>
                    <Mui.Box display="flex" alignItems="center" justifyContent="space-between" style={statusRowStyle}>
                        <Mui.Box display="flex" alignItems="center" style={{ gap: 10 }}>
                            <Icons.AlertTriangle size={16} style={{ color: "#424242" }} />
                            <Mui.Typography variant="body1" style={{ fontWeight: 700 }}>Errors</Mui.Typography>
                        </Mui.Box>
                        <Mui.Typography variant="body1" style={{ color: errorCount24h > 0 ? "#c62828" : undefined }}>
                            {backfillStatus === "running" && lastWebhookProcessed === null ? "–" : errorCount24h}
                        </Mui.Typography>
                    </Mui.Box>
                </Mui.Grid>

                {/* Leads (accordion) */}
                <Mui.Grid item>
                    <Mui.Box style={statusRowStyle}>
                        <Mui.Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            style={{ cursor: leadsMatched !== null ? "pointer" : "default" }}
                            onClick={() => leadsMatched !== null && this.setState({ leadsOpen: !leadsOpen })}
                        >
                            <Mui.Box display="flex" alignItems="center" style={{ gap: 10 }}>
                                <Icons.Users size={16} style={{ color: "#424242" }} />
                                <Mui.Typography variant="body1" style={{ fontWeight: 700 }}>Leads</Mui.Typography>
                            </Mui.Box>
                            <Mui.Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                <Mui.Typography variant="body1">
                                    {leadsMatched !== null ? `${leadsMatched} synced` : "–"}
                                </Mui.Typography>
                                {leadsMatched !== null && (
                                    leadsOpen ? <Icons.ChevronUp size={18} style={{ color: "#00796b" }} /> : <Icons.ChevronDown size={18} style={{ color: "#00796b" }} />
                                )}
                            </Mui.Box>
                        </Mui.Box>
                        <Mui.Collapse in={leadsOpen}>
                            <Mui.Box style={{ paddingTop: 24, paddingLeft: 26 }}>
                                <Mui.Divider />
                                <Mui.Box display="flex" alignItems="center" justifyContent="space-between" style={{ padding: "24px 0" }}>
                                    <Mui.Typography variant="body2" style={{ fontWeight: 600, color: "#5F6368" }}>Matched</Mui.Typography>
                                    <Mui.Typography variant="body2" style={{ color: "#5F6368" }}>{leadsMatched}</Mui.Typography>
                                </Mui.Box>
                                <Mui.Divider />
                                <Mui.Box display="flex" alignItems="center" justifyContent="space-between" style={{ padding: "24px 0" }}>
                                    <Mui.Typography variant="body2" style={{ fontWeight: 600, color: "#5F6368" }}>Unmatched</Mui.Typography>
                                    <Mui.Typography variant="body2" style={{ color: "#5F6368" }}>{leadsUnmatched ?? "–"}</Mui.Typography>
                                </Mui.Box>
                                {leadsUnmatched !== null && leadsUnmatched > 0 && (
                                    <Mui.Box
                                        display="flex"
                                        alignItems="center"
                                        style={{ gap: 8, backgroundColor: "#fff8e1", borderRadius: 4, padding: 16, marginBottom: 24 }}
                                    >
                                        <Icons.AlertTriangle size={14} style={{ color: "#f57f17", flexShrink: 0 }} />
                                        <Mui.Typography variant="body2">
                                            {copy.statusLeadsEmailHint}
                                        </Mui.Typography>
                                    </Mui.Box>
                                )}
                                <Mui.Divider />
                                <Mui.Box style={{ paddingTop: 24 }}>
                                    <Mui.Link
                                        // Pre-applies the Follow Up Boss source filter on the leads list (MAX-3997)
                                        href={urls.leads({ leadsPageFilters: { source: ["Follow Up Boss"] } })}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="body2"
                                        style={{ fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}
                                    >
                                        View leads sourced from Follow Up Boss <Icons.ExternalLink size={16} />
                                    </Mui.Link>
                                </Mui.Box>
                            </Mui.Box>
                        </Mui.Collapse>
                    </Mui.Box>
                </Mui.Grid>

                {/* Agents (accordion) */}
                <Mui.Grid item>
                    <Mui.Box style={statusRowStyle}>
                        <Mui.Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            style={{ cursor: agentsExpandable ? "pointer" : "default" }}
                            onClick={() => agentsExpandable && this.setState({ agentsOpen: !agentsOpen })}
                        >
                            <Mui.Box display="flex" alignItems="center" style={{ gap: 10 }}>
                                <Icons.User size={16} style={{ color: "#424242" }} />
                                <Mui.Typography variant="body1" style={{ fontWeight: 700 }}>Agents</Mui.Typography>
                            </Mui.Box>
                            <Mui.Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                {!selectionMode && agentsUnmatched !== null && agentsUnmatched > 0 && (
                                    <Icons.AlertTriangle size={15} style={{ color: "#f57f17" }} />
                                )}
                                <Mui.Typography variant="body1">
                                    {agentsSummary}
                                </Mui.Typography>
                                {agentsExpandable && (
                                    agentsOpen ? <Icons.ChevronUp size={18} style={{ color: "#00796b" }} /> : <Icons.ChevronDown size={18} style={{ color: "#00796b" }} />
                                )}
                            </Mui.Box>
                        </Mui.Box>
                        <Mui.Collapse in={agentsOpen}>
                            <Mui.Box style={{ paddingTop: 24, paddingLeft: 26 }}>
                                {selectionMode ? (
                                    <>
                                        <Mui.Divider />
                                        <Mui.Typography variant="body2" style={{ color: "#5F6368", padding: "24px 0" }}>
                                            {copy.statusSelectionHeading}
                                        </Mui.Typography>
                                        <Mui.Divider />
                                        {selectedPairs.map((pair, index) => (
                                            <React.Fragment key={pair.fubUserId}>
                                                <Mui.Typography variant="body2" style={{ color: "#1C1B1F", padding: "24px 0" }}>
                                                    <strong>{pair.userName}</strong>{pair.userEmail ? ` (${pair.userEmail})` : ""} → <strong>{pair.agentName}</strong>{pair.agentEmail ? ` (${pair.agentEmail})` : ""}
                                                </Mui.Typography>
                                                {/* the design leaves the last row's rule off, so it does not double up with the note */}
                                                {index < selectedPairs.length - 1 && <Mui.Divider />}
                                            </React.Fragment>
                                        ))}
                                        <Mui.Box
                                            display="flex"
                                            style={{ gap: 8, backgroundColor: designTokens.colors.tagInfoSurface, borderRadius: 8, padding: "12px 16px", marginTop: 16 }}
                                        >
                                            <Icons.Info size={16} style={{ color: designTokens.colors.tagInfoText, flexShrink: 0, marginTop: 3 }} />
                                            {/* MAX-4580: "contact support" is plain text here, never a link — a reseller's
                                                customer must not be sent to iHomefinder support (product decision 7) */}
                                            <Mui.Typography variant="body2">
                                                {selectedPairs.length === 0 ? NO_SELECTION_LOCKED_MESSAGE : SELECTION_LOCKED_MESSAGE}
                                            </Mui.Typography>
                                        </Mui.Box>
                                    </>
                                ) : (
                                    <>
                                    <Mui.Divider />
                                    <Mui.Box display="flex" alignItems="center" justifyContent="space-between" style={{ padding: "24px 0" }}>
                                        <Mui.Typography variant="body2" style={{ fontWeight: 600, color: "#5F6368" }}>Matched</Mui.Typography>
                                        <Mui.Typography variant="body2" style={{ color: "#5F6368" }}>{agentsMatched}</Mui.Typography>
                                    </Mui.Box>
                                    <Mui.Divider />
                                    <Mui.Box display="flex" alignItems="center" justifyContent="space-between" style={{ padding: "24px 0" }}>
                                        <Mui.Typography variant="body2" style={{ fontWeight: 600, color: "#5F6368" }}>Unmatched</Mui.Typography>
                                        <Mui.Typography variant="body2" style={{ color: "#5F6368" }}>{agentsUnmatched ?? "–"}</Mui.Typography>
                                    </Mui.Box>
                                    {agentsUnmatched !== null && agentsUnmatched > 0 && (
                                        <Mui.Box
                                            display="flex"
                                            alignItems="center"
                                            style={{ gap: 8, backgroundColor: "#fff8e1", borderRadius: 4, padding: 16 }}
                                        >
                                            <Icons.AlertTriangle size={14} style={{ color: "#f57f17", flexShrink: 0 }} />
                                            <Mui.Typography variant="body2">
                                                {copy.statusAgentsEmailHint}
                                            </Mui.Typography>
                                        </Mui.Box>
                                    )}
                                    {/* MAX-4028: fixed-seat plan with FUB users beyond the seat cap */}
                                    {this.isFixedSeatPlan() && (fubUsersNotInIhf ?? 0) > 0 && (
                                        <Mui.Box
                                            display="flex"
                                            alignItems="center"
                                            style={{ gap: 8, backgroundColor: "#fff8e1", borderRadius: 4, padding: 16, marginTop: 8 }}
                                        >
                                            <Icons.AlertTriangle size={14} style={{ color: "#f57f17", flexShrink: 0 }} />
                                            <Mui.Typography variant="body2">
                                                Unmatched users beyond your plan's agent limit won't sync automatically. Their leads route to your Follow Up Boss admin account. Contact us to upgrade.
                                            </Mui.Typography>
                                        </Mui.Box>
                                    )}
                                    </>
                                )}
                            </Mui.Box>
                        </Mui.Collapse>
                    </Mui.Box>
                </Mui.Grid>

            </Mui.Grid>
        );
    }

    public render() {
        const { connected } = this.props;
        const { setupComplete } = this.state;

        if (connected === null) {
            return (
                <Mui.Box display="flex" justifyContent="center" padding={4}>
                    <Mui.CircularProgress size={32} />
                </Mui.Box>
            );
        }

        if (!connected) {
            return setupComplete ? this.renderComplete() : this.renderWizard();
        }

        return this.renderConnected();
    }

}

export const ConnectionTab = connect(mapStateToProps, mapDispatchToProps)(Component);
